import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslations } from '../../contexts/Translations';
import { useApi, useApiFetch } from '../../contexts/Api';
import { Language } from 'ts-core';

export type AuthView = 'login' | 'register' | 'forgotPassword';

interface ForgotPasswordFormProps {
  setView: (view: AuthView) => void;
}

interface ForgotPasswordFormData {
  email: string;
}

export default function ForgotPasswordForm({ setView }: ForgotPasswordFormProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { translated, language } = useTranslations();
  const { apiEndpoints } = useApi();
  const apiFetch = useApiFetch();

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email(translated('email_invalid'))
      .required(translated('email_required')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(validationSchema),
  });

  const handleForgotPasswordSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setIsLoading(true);
      setServerError(null);
      setSuccessMessage(null);

      await apiFetch(apiEndpoints.forgotPassword, {
        method: 'POST',
        body: JSON.stringify({ email: data.email, language }),
      });

      // Always show success message to prevent email enumeration
      setSuccessMessage(
        translated('password_reset_email_sent') ||
          'If an account exists with this email, you will receive a password reset link.'
      );
    } catch (e: any) {
      // Always show success message to prevent email enumeration
      setSuccessMessage(
        translated('password_reset_email_sent') ||
          'If an account exists with this email, you will receive a password reset link.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-2">
      <p className="text-sm text-gray-600 mb-4">
        {translated('forgot_password_description') ||
          'Enter your email address and we will send you a link to reset your password.'}
      </p>

      <form
        onSubmit={handleSubmit(handleForgotPasswordSubmit)}
        className="flex flex-col items-end"
      >
        <input
          type="email"
          placeholder={translated('email')}
          {...register('email')}
          className="w-full border-2 border-gray-300 p-2 rounded-lg mb-2"
          disabled={isLoading || !!successMessage}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'email-error' : undefined}
        />
        {errors.email && (
          <p
            id="email-error"
            className="text-red-400 text-sm w-full text-left mb-2"
            role="alert"
          >
            {errors.email.message}
          </p>
        )}

        {serverError && (
          <p
            className="text-red-400 text-sm w-full text-left mb-2"
            role="alert"
          >
            {serverError}
          </p>
        )}

        {successMessage && (
          <p
            className="text-green-600 text-sm w-full text-left mb-2"
            role="status"
          >
            {successMessage}
          </p>
        )}

        {!successMessage && (
          <input
            className="bg-balloon text-white inline-flex justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            type="submit"
            value={
              isLoading
                ? translated('loading') || 'Loading...'
                : translated('send_reset_link') || 'Send Reset Link'
            }
            disabled={isLoading}
          />
        )}

        <button
          type="button"
          className="mt-2 text-balloon inline-flex justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          onClick={() => setView('login')}
          disabled={isLoading}
        >
          {translated('back_to_login') || 'Back to Login'}
        </button>
      </form>
    </div>
  );
}
